/**
 * PostsScreen — manage news/blog posts (Stage 3.3c).
 *
 * List view: every post with status + thumbnail, plus create/edit/delete.
 * Editor view: trilingual title/excerpt/rich-text body, a main photo, and a
 * reorderable set of extra photos. Text saves via GraphQL; photos upload via
 * the REST endpoint (/api/posts/upload/) — the backend converts each to WebP.
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Film,
  GripVertical,
  ImagePlus,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";

import { RichTextEditor } from "../components/RichTextEditor";
import { getToken } from "../auth/token";
import { cn } from "../lib/cn";
import { confirmDestructive, showError, showSuccess } from "../lib/sweet";

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ADMIN_POSTS = /* GraphQL */ `
  query AdminPosts {
    adminPosts {
      id
      title
      status
      publishedAt
      updatedAt
      mainImageUrl
      imageCount
    }
  }
`;

const ADMIN_POST = /* GraphQL */ `
  query AdminPost($id: ID!) {
    adminPost(id: $id) {
      id
      title titleKu titleAr
      excerpt excerptKu excerptAr
      body bodyKu bodyAr
      slug status publishedAt
      mainImageUrl
      images { id altText imageUrl sortOrder width height }
      videos { id url caption sortOrder }
    }
  }
`;

const CREATE_POST = /* GraphQL */ `
  mutation CreatePost($input: CreatePostInput!) {
    createPost(input: $input) { id }
  }
`;

const UPDATE_POST = /* GraphQL */ `
  mutation UpdatePost($id: ID!, $input: UpdatePostInput!) {
    updatePost(postId: $id, input: $input) { id }
  }
`;

const DELETE_POST = /* GraphQL */ `
  mutation DeletePost($id: ID!) { deletePost(postId: $id) }
`;

const PUBLISH_POST = /* GraphQL */ `
  mutation PublishPost($id: ID!) { publishPost(postId: $id) { id status } }
`;

const UNPUBLISH_POST = /* GraphQL */ `
  mutation UnpublishPost($id: ID!) { unpublishPost(postId: $id) { id status } }
`;

const DELETE_POST_IMAGE = /* GraphQL */ `
  mutation DeletePostImage($id: ID!) { deletePostImage(imageId: $id) }
`;

const REORDER_POST_IMAGES = /* GraphQL */ `
  mutation ReorderPostImages($postId: ID!, $ids: [ID!]!) {
    reorderPostImages(postId: $postId, imageIds: $ids) { id sortOrder }
  }
`;

const ADD_POST_VIDEO = /* GraphQL */ `
  mutation AddPostVideo($postId: ID!, $url: String!, $caption: String) {
    addPostVideo(postId: $postId, url: $url, caption: $caption) { id url caption sortOrder }
  }
`;

const DELETE_POST_VIDEO = /* GraphQL */ `
  mutation DeletePostVideo($id: ID!) { deletePostVideo(videoId: $id) }
`;

const REORDER_POST_VIDEOS = /* GraphQL */ `
  mutation ReorderPostVideos($postId: ID!, $ids: [ID!]!) {
    reorderPostVideos(postId: $postId, videoIds: $ids) { id sortOrder }
  }
`;

const API_ORIGIN = (() => {
  const env = import.meta.env.VITE_GRAPHQL_URL;
  if (env) return new URL(env).origin;
  return import.meta.env.PROD ? "https://api.kurdishrojava.org.uk" : "";
})();
const UPLOAD_URL = `${API_ORIGIN}/api/posts/upload/`;

function absUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_ORIGIN}${url}`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PostRow {
  id: string;
  title: string;
  status: string;
  publishedAt: string | null;
  updatedAt: string;
  mainImageUrl: string | null;
  imageCount: number;
}

interface PostImageRow {
  id: string;
  altText: string;
  imageUrl: string | null;
  sortOrder: number;
  width: number | null;
  height: number | null;
}

interface PostVideoRow {
  id: string;
  url: string;
  caption: string | null;
  sortOrder: number;
}

interface PostDetail {
  id: string;
  title: string; titleKu: string | null; titleAr: string | null;
  excerpt: string | null; excerptKu: string | null; excerptAr: string | null;
  body: string | null; bodyKu: string | null; bodyAr: string | null;
  slug: string; status: string; publishedAt: string | null;
  mainImageUrl: string | null;
  images: PostImageRow[];
  videos: PostVideoRow[];
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

type EditTarget = { id: string | null } | null;

export function PostsScreen() {
  const [editing, setEditing] = useState<EditTarget>(null);
  const [{ data, fetching }, refetch] = useQuery<{ adminPosts: PostRow[] }>({
    query: ADMIN_POSTS,
    requestPolicy: "cache-and-network",
  });

  if (editing) {
    return (
      <PostEditor
        key={editing.id ?? "new"}
        postId={editing.id}
        onClose={() => {
          setEditing(null);
          refetch({ requestPolicy: "network-only" });
        }}
      />
    );
  }

  const posts = data?.adminPosts ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">Posts</h1>
          <p className="mt-1 text-sm text-stone-500">
            News and announcements shown on the home page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ id: null })}
          className="inline-flex items-center gap-1.5 rounded-md bg-flag-red px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          <Plus className="size-4" /> New post
        </button>
      </header>

      {fetching && !data ? (
        <div className="rounded-lg border border-stone-200 bg-white p-12 text-center text-stone-500">
          <Loader2 className="mx-auto size-5 animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-12 text-center text-sm text-stone-500">
          No posts yet. Click <strong>New post</strong> to write one.
        </div>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white p-3"
            >
              <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-stone-100">
                {p.mainImageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={absUrl(p.mainImageUrl)} alt="" className="size-full object-cover" />
                ) : (
                  <div className="flex size-full items-center justify-center text-stone-300">
                    <ImagePlus className="size-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-stone-900">{p.title}</p>
                <p className="mt-0.5 text-xs text-stone-500">
                  {p.imageCount} photo{p.imageCount === 1 ? "" : "s"} ·{" "}
                  {p.publishedAt ? new Date(p.publishedAt).toLocaleDateString() : "No date set"}
                </p>
              </div>
              <StatusBadge status={p.status} />
              <button
                type="button"
                onClick={() => setEditing({ id: p.id })}
                className="inline-flex size-8 items-center justify-center rounded-md border border-stone-200 text-stone-500 hover:bg-stone-50 hover:text-stone-700"
                aria-label="Edit post"
              >
                <Pencil className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    published: "bg-green-100 text-green-800 border-green-300",
    draft: "bg-stone-200 text-stone-700 border-stone-300",
    archived: "bg-red-100 text-red-800 border-red-300",
  };
  return (
    <span className={cn("rounded-full border px-2.5 py-0.5 text-xs capitalize", map[status] ?? map.draft)}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const EMPTY = {
  title: "", titleKu: "", titleAr: "",
  excerpt: "", excerptKu: "", excerptAr: "",
  body: "", bodyKu: "", bodyAr: "",
};

function PostEditor({ postId, onClose }: { postId: string | null; onClose: () => void }) {
  const [currentId, setCurrentId] = useState<string | null>(postId);
  const [form, setForm] = useState({ ...EMPTY });
  const [status, setStatus] = useState<string>("draft");
  const [postDate, setPostDate] = useState<string>("");
  const [mainImageUrl, setMainImageUrl] = useState<string | null>(null);
  const [images, setImages] = useState<PostImageRow[]>([]);
  const [videos, setVideos] = useState<PostVideoRow[]>([]);
  const [saving, setSaving] = useState(false);

  const [{ data, fetching }, refetchPost] = useQuery<{ adminPost: PostDetail | null }>({
    query: ADMIN_POST,
    variables: { id: currentId ?? "" },
    pause: !currentId,
    requestPolicy: "cache-and-network",
  });

  // Populate form when a post loads.
  useEffect(() => {
    const p = data?.adminPost;
    if (!p) return;
    setForm({
      title: p.title ?? "", titleKu: p.titleKu ?? "", titleAr: p.titleAr ?? "",
      excerpt: p.excerpt ?? "", excerptKu: p.excerptKu ?? "", excerptAr: p.excerptAr ?? "",
      body: p.body ?? "", bodyKu: p.bodyKu ?? "", bodyAr: p.bodyAr ?? "",
    });
    setStatus(p.status);
    setPostDate(p.publishedAt ? p.publishedAt.slice(0, 10) : "");
    setMainImageUrl(p.mainImageUrl);
    setImages([...p.images].sort((a, b) => a.sortOrder - b.sortOrder));
    setVideos([...p.videos].sort((a, b) => a.sortOrder - b.sortOrder));
  }, [data]);

  const [, createPost] = useMutation(CREATE_POST);
  const [, updatePost] = useMutation(UPDATE_POST);
  const [, deletePost] = useMutation(DELETE_POST);
  const [, publishPost] = useMutation(PUBLISH_POST);
  const [, unpublishPost] = useMutation(UNPUBLISH_POST);
  const [, deleteImage] = useMutation(DELETE_POST_IMAGE);
  const [, reorderImages] = useMutation(REORDER_POST_IMAGES);
  const [, addVideo] = useMutation(ADD_POST_VIDEO);
  const [, deleteVideo] = useMutation(DELETE_POST_VIDEO);
  const [, reorderVideos] = useMutation(REORDER_POST_VIDEOS);

  function set<K extends keyof typeof EMPTY>(key: K, val: string) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleSave(): Promise<string | null> {
    if (!form.title.trim()) {
      await showError({ title: "Title is required", text: "Give the post an English title." });
      return null;
    }
    setSaving(true);
    try {
      const input = {
        title: form.title.trim(),
        titleKu: form.titleKu.trim(),
        titleAr: form.titleAr.trim(),
        excerpt: form.excerpt.trim(),
        excerptKu: form.excerptKu.trim(),
        excerptAr: form.excerptAr.trim(),
        body: form.body,
        bodyKu: form.bodyKu,
        bodyAr: form.bodyAr,
        publishedAt: postDate ? new Date(`${postDate}T12:00:00`).toISOString() : null,
      };
      if (!currentId) {
        const res = await createPost({ input });
        if (res.error) {
          await showError({ title: "Couldn't create post", text: res.error.message });
          return null;
        }
        const newId = (res.data as { createPost: { id: string } }).createPost.id;
        setCurrentId(newId);
        showSuccess({ title: "Draft saved" });
        return newId;
      } else {
        const res = await updatePost({ id: currentId, input });
        if (res.error) {
          await showError({ title: "Couldn't save", text: res.error.message });
          return null;
        }
        showSuccess({ title: "Saved" });
        return currentId;
      }
    } finally {
      setSaving(false);
    }
  }

  async function handlePublishToggle() {
    const id = currentId ?? (await handleSave());
    if (!id) return;
    const res = status === "published"
      ? await unpublishPost({ id })
      : await publishPost({ id });
    if (res.error) {
      await showError({ title: "Couldn't change status", text: res.error.message });
      return;
    }
    setStatus(status === "published" ? "draft" : "published");
    showSuccess({ title: status === "published" ? "Unpublished" : "Published" });
  }

  async function handleDeletePost() {
    if (!currentId) {
      onClose();
      return;
    }
    const ok = await confirmDestructive({
      title: "Delete this post?",
      text: `"${form.title}"\n\nThis removes the post and all its photos. It cannot be undone.`,
      confirmText: "Yes, delete",
    });
    if (!ok) return;
    const res = await deletePost({ id: currentId });
    if (res.error) {
      await showError({ title: "Couldn't delete", text: res.error.message });
      return;
    }
    showSuccess({ title: "Deleted" });
    onClose();
  }

  async function handleDeleteImage(img: PostImageRow) {
    const ok = await confirmDestructive({
      title: "Delete this photo?",
      text: `"${img.altText}"`,
      confirmText: "Yes, delete",
    });
    if (!ok) return;
    const res = await deleteImage({ id: img.id });
    if (res.error) {
      await showError({ title: "Couldn't delete", text: res.error.message });
      return;
    }
    setImages((arr) => arr.filter((i) => i.id !== img.id));
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  async function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !currentId) return;
    const oldIndex = images.findIndex((i) => i.id === active.id);
    const newIndex = images.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(images, oldIndex, newIndex);
    setImages(next);
    const res = await reorderImages({ postId: currentId, ids: next.map((i) => i.id) });
    if (res.error) {
      setImages(images);
      await showError({ title: "Couldn't save order", text: res.error.message });
    }
  }

  async function handleAddVideo(url: string, caption: string): Promise<boolean> {
    if (!currentId) return false;
    const res = await addVideo({ postId: currentId, url, caption: caption || null });
    if (res.error) {
      await showError({ title: "Couldn't add video", text: res.error.message });
      return false;
    }
    refetchPost({ requestPolicy: "network-only" });
    showSuccess({ title: "Video added" });
    return true;
  }

  async function handleDeleteVideo(v: PostVideoRow) {
    const ok = await confirmDestructive({
      title: "Remove this video?",
      text: v.caption || v.url,
      confirmText: "Yes, remove",
    });
    if (!ok) return;
    const res = await deleteVideo({ id: v.id });
    if (res.error) {
      await showError({ title: "Couldn't remove", text: res.error.message });
      return;
    }
    setVideos((arr) => arr.filter((x) => x.id !== v.id));
  }

  async function handleVideoDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !currentId) return;
    const oldIndex = videos.findIndex((v) => v.id === active.id);
    const newIndex = videos.findIndex((v) => v.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(videos, oldIndex, newIndex);
    setVideos(next);
    const res = await reorderVideos({ postId: currentId, ids: next.map((v) => v.id) });
    if (res.error) {
      setVideos(videos);
      await showError({ title: "Couldn't save order", text: res.error.message });
    }
  }

  const labelClass = "block text-xs font-medium text-stone-600";
  const inputClass =
    "mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  if (currentId && fetching && !data) {
    return (
      <div className="rounded-lg border border-stone-200 bg-white p-12 text-center text-stone-500">
        <Loader2 className="mx-auto size-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-12">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-flag-red"
        >
          <ArrowLeft className="size-4" /> All posts
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge status={status} />
          <button
            type="button"
            onClick={handlePublishToggle}
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
          >
            {status === "published" ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            {status === "published" ? "Unpublish" : "Publish"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Title (trilingual) */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-stone-500">Title</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className={labelClass}>English *
            <input className={inputClass} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </label>
          <label className={labelClass}>Kurmancî
            <input className={inputClass} value={form.titleKu} onChange={(e) => set("titleKu", e.target.value)} />
          </label>
          <label className={labelClass}>العربية
            <input dir="rtl" className={inputClass} value={form.titleAr} onChange={(e) => set("titleAr", e.target.value)} />
          </label>
        </div>
      </section>

      {/* Post date */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-stone-500">Post date</h2>
        <p className="mb-3 text-xs text-stone-400">The date shown on the post card and page.</p>
        <input
          type="date"
          value={postDate}
          onChange={(e) => setPostDate(e.target.value)}
          className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
        />
      </section>

      {/* Excerpt (trilingual) */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-stone-500">Short summary</h2>
        <p className="mb-3 text-xs text-stone-400">Shown on the post card on the home page.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className={labelClass}>English
            <textarea rows={3} className={inputClass} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </label>
          <label className={labelClass}>Kurmancî
            <textarea rows={3} className={inputClass} value={form.excerptKu} onChange={(e) => set("excerptKu", e.target.value)} />
          </label>
          <label className={labelClass}>العربية
            <textarea dir="rtl" rows={3} className={inputClass} value={form.excerptAr} onChange={(e) => set("excerptAr", e.target.value)} />
          </label>
        </div>
      </section>

      {/* Main photo */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-stone-500">Main photo</h2>
        {!currentId ? (
          <p className="text-sm text-stone-500">Save the draft first, then you can add photos.</p>
        ) : (
          <MainPhoto
            postId={currentId}
            url={absUrl(mainImageUrl)}
            onUploaded={() => refetchPost({ requestPolicy: "network-only" })}
          />
        )}
      </section>

      {/* Body (trilingual rich text) */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-stone-500">Article text</h2>
        <div className="space-y-4">
          <div>
            <p className={cn(labelClass, "mb-1")}>English</p>
            <RichTextEditor value={form.body} onChange={(v) => set("body", v)} />
          </div>
          <div>
            <p className={cn(labelClass, "mb-1")}>Kurmancî</p>
            <RichTextEditor value={form.bodyKu} onChange={(v) => set("bodyKu", v)} />
          </div>
          <div>
            <p className={cn(labelClass, "mb-1")}>العربية</p>
            <RichTextEditor value={form.bodyAr} onChange={(v) => set("bodyAr", v)} dir="rtl" />
          </div>
        </div>
      </section>

      {/* Gallery photos */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-stone-500">More photos</h2>
        {!currentId ? (
          <p className="text-sm text-stone-500">Save the draft first, then you can add photos.</p>
        ) : (
          <>
            <GalleryUploader
              postId={currentId}
              onUploaded={() => refetchPost({ requestPolicy: "network-only" })}
            />
            {images.length === 0 ? (
              <p className="mt-3 text-sm text-stone-400">No extra photos yet.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={images.map((i) => i.id)} strategy={rectSortingStrategy}>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {images.map((img) => (
                      <SortablePhoto key={img.id} img={img} onDelete={() => handleDeleteImage(img)} />
                    ))}
                  </div>
                </SortableContext>
                <p className="mt-2 text-xs text-stone-500">Drag to reorder. Order saves automatically.</p>
              </DndContext>
            )}
          </>
        )}
      </section>

      {/* Videos (Facebook) */}
      <section className="rounded-lg border border-stone-200 bg-white p-4">
        <h2 className="mb-1 text-xs font-medium uppercase tracking-wide text-stone-500">Videos</h2>
        <p className="mb-3 text-xs text-stone-400">
          Paste a Facebook or YouTube link (facebook.com, fb.watch, youtube.com, or youtu.be).
          The video must be public.
        </p>
        {!currentId ? (
          <p className="text-sm text-stone-500">Save the draft first, then you can add videos.</p>
        ) : (
          <>
            <VideoAdder onAdd={handleAddVideo} />
            {videos.length === 0 ? (
              <p className="mt-3 text-sm text-stone-400">No videos yet.</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleVideoDragEnd}>
                <SortableContext items={videos.map((v) => v.id)} strategy={verticalListSortingStrategy}>
                  <ul className="mt-3 space-y-2">
                    {videos.map((v) => (
                      <SortableVideo key={v.id} video={v} onDelete={() => handleDeleteVideo(v)} />
                    ))}
                  </ul>
                </SortableContext>
                <p className="mt-2 text-xs text-stone-500">Drag to reorder. Order saves automatically.</p>
              </DndContext>
            )}
          </>
        )}
      </section>

      {/* Danger zone */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleDeletePost}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
        >
          <Trash2 className="size-4" /> Delete post
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main photo uploader
// ---------------------------------------------------------------------------

function MainPhoto({ postId, url, onUploaded }: { postId: string; url?: string; onUploaded: () => void }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  async function upload(file: File) {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("post_id", postId);
      form.append("kind", "main");
      form.append("file", file, file.name);
      const token = getToken();
      const res = await fetch(UPLOAD_URL, {
        method: "POST",
        headers: token ? { Authorization: `JWT ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        let m = `${res.status}`;
        try { const b = await res.json(); m = b?.errors?.[0]?.message ?? m; } catch { /* */ }
        await showError({ title: "Upload failed", text: m });
        return;
      }
      onUploaded();
      showSuccess({ title: "Main photo updated" });
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="h-28 w-44 overflow-hidden rounded-md border border-stone-200 bg-stone-100">
        {url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={url} alt="" className="size-full object-cover" />
        ) : (
          <div className="flex size-full items-center justify-center text-stone-300">
            <ImagePlus className="size-6" />
          </div>
        )}
      </div>
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm hover:bg-stone-50">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4 text-stone-500" />}
        {url ? "Replace photo" : "Upload main photo"}
        <input
          ref={ref}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
        />
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery uploader
// ---------------------------------------------------------------------------

function GalleryUploader({ postId, onUploaded }: { postId: string; onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { await showError({ title: "Pick a file first" }); return; }
    if (!alt.trim()) { await showError({ title: "Alt text is required", text: "Screen readers rely on it." }); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("post_id", postId);
      form.append("kind", "gallery");
      form.append("alt_text", alt.trim());
      form.append("file", file, file.name);
      const token = getToken();
      const res = await fetch(UPLOAD_URL, {
        method: "POST",
        headers: token ? { Authorization: `JWT ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        let m = `${res.status}`;
        try { const b = await res.json(); m = b?.errors?.[0]?.message ?? m; } catch { /* */ }
        await showError({ title: "Upload failed", text: m });
        return;
      }
      setFile(null); setAlt("");
      if (ref.current) ref.current.value = "";
      onUploaded();
      showSuccess({ title: "Photo added" });
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm hover:bg-stone-50">
        <ImagePlus className="size-4 text-stone-500" />
        <span className="truncate">{file ? file.name : "Choose photo"}</span>
        <input
          ref={ref}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      <input
        type="text"
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        placeholder="Alt text (required) — describe the photo"
        className={inputClass}
      />
      <button
        type="submit"
        disabled={busy || !file || !alt.trim()}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy && <Loader2 className="size-4 animate-spin" />}
        {busy ? "Adding…" : "Add photo"}
      </button>
    </form>
  );
}

function VideoAdder({ onAdd }: { onAdd: (url: string, caption: string) => Promise<boolean> }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) { await showError({ title: "Paste a Facebook video link" }); return; }
    setBusy(true);
    try {
      const ok = await onAdd(url.trim(), caption.trim());
      if (ok) { setUrl(""); setCaption(""); }
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    "w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_auto]">
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://www.facebook.com/… or https://youtu.be/…"
        className={inputClass}
      />
      <input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="Caption (optional)"
        className={inputClass}
      />
      <button
        type="submit"
        disabled={busy || !url.trim()}
        className="inline-flex items-center justify-center gap-2 rounded-md bg-flag-red px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
        {busy ? "Adding…" : "Add video"}
      </button>
    </form>
  );
}

function SortableVideo({ video, onDelete }: { video: PostVideoRow; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: video.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-md border bg-white p-2",
        isDragging ? "border-flag-red ring-2 ring-flag-red/30" : "border-stone-200",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab touch-none text-stone-400 hover:text-stone-600 active:cursor-grabbing"
        aria-label="Reorder video"
      >
        <GripVertical className="size-5" />
      </button>
      <Film className="size-5 shrink-0 text-stone-400" />
      <div className="min-w-0 flex-1">
        {video.caption && <p className="truncate text-sm font-medium text-stone-800">{video.caption}</p>}
        <p className="truncate text-xs text-stone-500" title={video.url}>{video.url}</p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="inline-flex size-8 items-center justify-center rounded-md border border-stone-200 text-stone-500 hover:bg-red-50 hover:text-red-700"
        aria-label="Remove video"
      >
        <Trash2 className="size-4" />
      </button>
    </li>
  );
}

function SortablePhoto({ img, onDelete }: { img: PostImageRow; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative overflow-hidden rounded-md border bg-white",
        isDragging ? "border-flag-red ring-2 ring-flag-red/30" : "border-stone-200",
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="block aspect-square w-full cursor-grab touch-none active:cursor-grabbing"
        aria-label={`Reorder ${img.altText}`}
      >
        {img.imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={absUrl(img.imageUrl)} alt={img.altText} className="size-full object-cover" draggable={false} />
        ) : null}
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-1.5 top-1.5 inline-flex size-7 items-center justify-center rounded-full bg-stone-900/70 text-white opacity-0 transition-opacity hover:bg-red-700 group-hover:opacity-100 focus:opacity-100"
        aria-label={`Delete ${img.altText}`}
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  );
}
